import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, EMPTY, filter, groupBy, map, mergeMap, of, switchMap, take} from 'rxjs';
import {Store} from '@ngrx/store';
import {EmailversandService} from '../../openapi/fakturierung-openapi';
import {VersandstatusHistoryActions} from '../actions/versandstatus-history.actions';
import {FakturierungsbelegEntitiesActions} from '../actions/fakturierungsbeleg-entities.actions';
import {AppState} from '../states/app.state';
import {VersandstatusHistorySelectors} from '../selectors/versandstatus-history.selectors';


/**
 * Page-Size fuer die Versandstatus-Historie. Wird sowohl beim initialen Load
 * (Preview-Route) als auch beim Polling-Reload-Effect verwendet, damit der
 * Dialog mit Vollhistorie keine inkonsistenten Daten sieht.
 */
export const VERSANDSTATUS_HISTORIE_PAGE_SIZE = 50;

/**
 * Effects fuer das Laden der Versandstatus-Historie eines Belegs via separatem
 * Backend-Endpoint. Der Beleg traegt nur den aktuellen Versandstatus; die
 * vollstaendige Historie wird hier on-demand nachgeladen.
 */
@Injectable()
export class VersandstatusHistoryEffects {

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<AppState>,
    private readonly emailversandService: EmailversandService,
  ) {
  }

  readonly loadVersandstatusHistorie$ = createEffect(() => this.actions$.pipe(
    ofType(VersandstatusHistoryActions.loadVersandstatusHistorie),
    switchMap(({betriebId, belegId, pageable}) =>
      this.emailversandService.readVersandstatusHistorie(betriebId, belegId, pageable).pipe(
        map(page => VersandstatusHistoryActions.loadVersandstatusHistorieSuccess({
          belegId,
          page,
        })),
        catchError(error => of(VersandstatusHistoryActions.loadVersandstatusHistorieFailure({
          belegId,
          error,
        }))),
      ),
    ),
  ));

  /**
   * Laedt die Versandstatus-Historie neu, sobald der zugehoerige Beleg via
   * getFakturierungsbelegById-Success aktualisiert wurde — aber nur, wenn die
   * Historie bereits fuer diesen Beleg im Store steht (d.h. die Preview-Seite
   * ist aktuell offen). So bleibt das Reload an den Lifecycle der Preview
   * gekoppelt und triggert nicht unbeteiligte Store-Updates.
   *
   * Deckt implizit auch den Nach-Versand-Fall ab: versendeBelegSuccess
   * dispatcht selbst getFakturierungsbelegById — der Success-Roundtrip
   * triggert diesen Effect.
   *
   * Das Action-Stream ist via `groupBy(belegId)` partitioniert und pro
   * Partition mit `switchMap` aufgeloest. So koennen parallele Beleg-Refreshes
   * unterschiedlicher Belege nicht gegenseitig den Reload-Trigger
   * "wegcanceln". Innerhalb derselben belegId bleibt `switchMap` erhalten —
   * doppelt-rasche Reloads desselben Belegs werden bewusst zusammengefasst.
   */
  readonly reloadHistorieAfterBelegChange$ = createEffect(() => this.actions$.pipe(
    ofType(FakturierungsbelegEntitiesActions.getFakturierungsbelegByIdSuccess),
    filter(({belegDto}) => !!belegDto?.id),
    groupBy(({belegDto}) => belegDto.id),
    mergeMap(group => group.pipe(
      switchMap(({belegDto}) =>
        this.store.select(VersandstatusHistorySelectors.isLoadedForBeleg(belegDto.id)).pipe(
          take(1),
          switchMap(isLoaded => isLoaded
            ? of(VersandstatusHistoryActions.loadVersandstatusHistorie({
              betriebId: belegDto.betriebId,
              belegId: belegDto.id,
              pageable: {number: 0, size: VERSANDSTATUS_HISTORIE_PAGE_SIZE},
            }))
            : EMPTY),
        ),
      ),
    )),
  ));
}
